import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { MultiSelect, SingleSelect } from "../Select"
import RangePicker from "../RangePicker/RangePicker"
import FilterField from "./FilterField"
import { buildDisplayResultsOptions } from "../../utils/options"

const FiltersContent = ({
  pf,
  setPF,
  setPFCond,
  openSelectId,
  setOpenSelectId,
  onApply,
  onClear,
  OPT,
  permissions,
}) => {
  const { t } = useTranslation()

  const displayResultsOptions = useMemo(
    () => buildDisplayResultsOptions(permissions, t),
    [permissions, t]
  )
  return (
    <div className="p-4 space-y-3 overflow-y-auto">
      {/* Row 1 */}
      <div className="flex flex-wrap gap-2">
        {/* Поиск */}
        <div style={{ flex: "2 1 200px", minWidth: 180 }}>
          <p className="text-xs text-gray-500 font-medium mb-1.5">{t("requests.search")}</p>
          <div className="flex gap-1.5">
            <div style={{ width: 120, flexShrink: 0 }}>
              <SingleSelect
                id="searchBy"
                openId={openSelectId}
                setOpenId={setOpenSelectId}
                label={t("requests.search_object.promt")}
                options={OPT.searchBy}
                value={pf.searchBy}
                onChange={(v) => setPF("searchBy", v)}
              />
            </div>
            <input
              type="text"
              placeholder="..."
              value={pf.search}
              onChange={(e) => setPF("search", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && onApply()}
              className="flex-1 min-w-0 px-2.5 py-2 bg-white border border-gray-200 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
          </div>
        </div>

        {/* Статус */}
        <FilterField label={t("requests.status")} grow={2} minW={150}>
          <MultiSelect
            id="status"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("admin.status")}
            options={OPT.status}
            value={pf.status}
            onChange={(v) => setPF("status", v)}
          />
        </FilterField>

        {/* Продавцы / Поставщики */}
        <div style={{ flex: "2 1 200px", minWidth: 180 }}>
          <p className="text-xs text-gray-500 font-medium mb-1.5">
            {t("admin.sellers")} / {t("admin.providers")}
          </p>
          <div className="flex gap-1.5">
            <MultiSelect
              id="sellers"
              openId={openSelectId}
              setOpenId={setOpenSelectId}
              label={t("admin.all_sellers")}
              options={OPT.sellers}
              value={pf.sellers}
              onChange={(v) => setPF("sellers", v)}
            />
            <MultiSelect
              id="suppliers"
              openId={openSelectId}
              setOpenId={setOpenSelectId}
              label={t("admin.all_providers")}
              options={OPT.suppliers}
              value={pf.suppliers}
              onChange={(v) => setPF("suppliers", v)}
            />
          </div>
        </div>

        {/* Пользователи */}
        <FilterField label={t("admin.users")} grow={1} minW={110}>
          <MultiSelect
            id="users"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("admin.users")}
            options={OPT.users}
            value={pf.users}
            onChange={(v) => setPF("users", v)}
          />
        </FilterField>

        {/* Модератор */}
        <FilterField label={t("admin.moderator")} grow={1} minW={110}>
          <SingleSelect
            id="moderator"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("admin.all")}
            options={OPT.moderator}
            value={pf.moderator}
            onChange={(v) => setPF("moderator", v)}
          />
        </FilterField>

        <FilterField label={t("admin.rialto")} grow={1} minW={90}>
          <MultiSelect
            id="market"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("filter.market")}
            options={OPT.market}
            value={pf.market}
            onChange={(v) => setPF("market", v)}
          />
        </FilterField>

        <FilterField label={t("admin.states")} grow={1} minW={110}>
          <SingleSelect
            id="states"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("admin.all_states")}
            options={OPT.states}
            value={pf.states}
            onChange={(v) => setPF("states", v)}
          />
        </FilterField>
      </div>

      <div className="flex flex-wrap gap-2">
        {/* Даты */}
        <FilterField label={t("requests.interval_requests")} grow={2} minW={160}>
          <RangePicker
            id="dateRequest"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("filter.select_range")}
            value={pf.dateRequest}
            onChange={(v) => setPF("dateRequest", v)}
          />
        </FilterField>

        <FilterField label={t("requests.interval_check_in")} grow={2} minW={160}>
          <RangePicker
            id="dateCheckin"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("filter.select_range")}
            value={pf.dateCheckin}
            onChange={(v) => setPF("dateCheckin", v)}
          />
        </FilterField>

        <FilterField label={t("requests.interval_check_out")} grow={2} minW={160}>
          <RangePicker
            id="dateCheckout"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("filter.select_range")}
            value={pf.dateCheckout}
            onChange={(v) => setPF("dateCheckout", v)}
          />
        </FilterField>

        <div style={{ flex: "2 1 180px", minWidth: 160 }}>
          <p className="text-xs text-gray-500 font-medium mb-1.5">
            {t("admin.modules")} / {t("admin.services")}
          </p>
          <div className="flex gap-1.5">
            <SingleSelect
              id="modules"
              openId={openSelectId}
              setOpenId={setOpenSelectId}
              label={t("admin.modules")}
              options={OPT.modules}
              value={pf.modules}
              onChange={(v) => setPF("modules", v)}
            />
            <MultiSelect
              id="services"
              openId={openSelectId}
              setOpenId={setOpenSelectId}
              label={t("admin.services")}
              options={OPT.services}
              value={pf.services}
              onChange={(v) => setPF("services", v)}
            />
          </div>
        </div>

        {/* Пакет */}
        <FilterField label={t("filter.package")} grow={1} minW={110}>
          <MultiSelect
            id="package"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("filter.package")}
            options={OPT.modules.filter((m) => m.value !== "")}
            value={pf.package}
            onChange={(v) => setPF("package", v)}
          />
        </FilterField>

        <FilterField label={t("admin.referer")} grow={1} minW={110}>
          <MultiSelect
            id="referer"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("admin.referer")}
            options={OPT.referer}
            value={pf.referer}
            onChange={(v) => setPF("referer", v)}
          />
        </FilterField>

        <FilterField label={t("admin.payment_status")} grow={1} minW={110}>
          <SingleSelect
            id="paymentStatus"
            openId={openSelectId}
            setOpenId={setOpenSelectId}
            label={t("admin.all")}
            options={OPT.paymentStatus}
            value={pf.paymentStatus}
            onChange={(v) => setPF("paymentStatus", v)}
          />
        </FilterField>

        <div style={{ flex: "2 1 180px", minWidth: 160 }}>
          <p className="text-xs text-gray-500 font-medium mb-1.5">{t("admin.display_results")}</p>
          <div className="flex gap-1.5">
            <SingleSelect
              id="displayResults"
              openId={openSelectId}
              setOpenId={setOpenSelectId}
              label={t("trainees.on_screen")}
              options={displayResultsOptions}
              value={pf.displayResults}
              onChange={(v) => setPF("displayResults", v)}
            />
            <SingleSelect
              id="roomStatus"
              openId={openSelectId}
              setOpenId={setOpenSelectId}
              label={t("filter.room_status")}
              options={OPT.roomStatus}
              value={pf.roomStatus}
              onChange={(v) => setPF("roomStatus", v)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-xs text-gray-500 font-medium">{t("admin.condition")}:</span>
          {[
            ["deleted", t("admin.deleted")],
            ["archive", t("admin.archive")],
            ["ticketsIssued", t("admin.tickets_issued")],
            ["ticketsNotIssued", t("admin.tickets_not_issued")],
            ["competitorPrice", t("admin.competitor_price_request")],
          ].map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={pf.condition[k]}
                onChange={(e) => setPFCond(k, e.target.checked)}
                className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-600">{l}</span>
            </label>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <span className="text-xs text-gray-500 font-medium">{t("admin.direction")}:</span>
          {[
            ["incoming", `↓ ${t("admin.incoming")}`],
            ["outgoing", `↑ ${t("admin.outgoing")}`],
            ["any", t("admin.any")],
          ].map(([v, l]) => (
            <label key={v} className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="radio"
                name="direction"
                value={v}
                checked={pf.direction === v}
                onChange={() => setPF("direction", v)}
                className="w-3.5 h-3.5 border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-600">{l}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Кнопки */}
      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-medium text-gray-500 hover:text-red-500 hover:border-red-200 hover:bg-red-50 transition-all"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
          {t("admin.clear_filter")}
        </button>
        <button
          type="button"
          onClick={onApply}
          className="flex items-center justify-center gap-1.5 px-5 py-2 bg-blue-600 text-white rounded-xl text-xs font-medium hover:bg-blue-700 active:scale-95 transition-all shadow-sm shadow-blue-100"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {t("requests.filter")}
        </button>
      </div>
    </div>
  )
}

export default FiltersContent
